import { EikoLogo } from '@/components/EikoLogo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import { useTenantSlug } from '@/hooks/useTenantSlug';
import { cn } from '@/lib/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Link, Navigate } from 'react-router-dom';
import { z } from 'zod';

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

type LoginFormData = z.infer<typeof loginSchema>;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20, filter: 'blur(4px)' },
  visible: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94] as const,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, scale: 0.95, filter: 'blur(8px)' },
  visible: {
    opacity: 1,
    scale: 1,
    filter: 'blur(0px)',
    transition: {
      duration: 0.6,
      ease: [0.25, 0.46, 0.45, 0.94] as const,
    },
  },
};

const inputClassName = cn(
  'h-11 rounded-lg border-stone-200 bg-white',
  'text-stone-900 placeholder:text-stone-400',
  'transition-all duration-300',
  'focus-visible:border-emerald-500/50',
  'focus-visible:ring-[3px] focus-visible:ring-emerald-500/15',
  'focus-visible:bg-white',
  'hover:border-stone-300',
);

export function LoginView({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  const { login, isLoading, isAuthenticated } = useAuth();
  const { tenantSlug } = useTenantSlug();

  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  if (isAuthenticated() && tenantSlug) {
    return <Navigate to={`/o/${tenantSlug}/home`} replace />;
  }

  const handleSubmit = (data: LoginFormData) => {
    login({
      email: data.email,
      password: data.password,
    });
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-stone-50">

      {/* Card */}
      <motion.div
        variants={cardVariants}
        initial="hidden"
        animate="visible"
        className="relative z-10 w-full max-w-[420px] mx-4"
      >
        <div
          className={cn(
            'rounded-2xl border border-stone-200 p-8 sm:p-10',
            'bg-white',
            'shadow-sm',
          )}
        >
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="flex flex-col"
          >
            {/* Brand */}
            <motion.div variants={itemVariants} className="mb-8 flex justify-center">
              <EikoLogo className="h-14 text-emerald-600" />
            </motion.div>

            {/* Title */}
            <motion.div variants={itemVariants} className="mb-6 text-center">
              <h2 className="text-lg font-medium text-stone-800">
                Connectez-vous à votre compte
              </h2>
              <p className="mt-1.5 text-sm text-stone-500">
                Entrez votre email ci-dessous pour vous connecter
              </p>
            </motion.div>

            {/* Form */}
            <div className={cn('grid gap-4', className)} {...props}>
              <form onSubmit={form.handleSubmit(handleSubmit)}>
                <div className="grid gap-4">
                  {/* Email */}
                  <motion.div variants={itemVariants} className="grid gap-2">
                    <Label htmlFor="email" className="text-stone-600 text-sm">
                      Email
                    </Label>
                    <Input
                      {...form.register('email')}
                      id="email"
                      placeholder="nom@exemple.com"
                      type="email"
                      autoCapitalize="none"
                      autoComplete="email"
                      autoCorrect="off"
                      disabled={isLoading}
                      className={inputClassName}
                    />
                    {form.formState.errors.email && (
                      <p className="text-sm text-red-400">
                        {form.formState.errors.email.message}
                      </p>
                    )}
                  </motion.div>

                  {/* Password */}
                  <motion.div variants={itemVariants} className="grid gap-2">
                    <Label
                      htmlFor="password"
                      className="text-stone-600 text-sm"
                    >
                      Mot de passe
                    </Label>
                    <Input
                      {...form.register('password')}
                      id="password"
                      placeholder="Mot de passe"
                      type="password"
                      autoCapitalize="none"
                      autoComplete="current-password"
                      disabled={isLoading}
                      className={inputClassName}
                    />
                    {form.formState.errors.password && (
                      <p className="text-sm text-red-400">
                        {form.formState.errors.password.message}
                      </p>
                    )}
                  </motion.div>

                  {/* Submit */}
                  <motion.div variants={itemVariants} className="pt-2">
                    <Button
                      type="submit"
                      disabled={isLoading}
                      className={cn(
                        'relative w-full h-11 rounded-lg text-sm font-semibold',
                        'bg-emerald-600 text-white',
                        'hover:bg-emerald-700',
                        'transition-all duration-300',
                        'disabled:opacity-50',
                      )}
                    >
                      {isLoading ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        'Se connecter'
                      )}
                    </Button>
                  </motion.div>

                  {/* Forgot password */}
                  <motion.div
                    variants={itemVariants}
                    className="text-center pt-2"
                  >
                    <Link
                      to="/forgot-password"
                      className="text-sm text-stone-400 transition-colors duration-200 hover:text-emerald-600"
                    >
                      Mot de passe oublié ?
                    </Link>
                  </motion.div>
                </div>
              </form>
            </div>

            {/* Terms */}
            <motion.p
              variants={itemVariants}
              className="mt-6 px-2 text-center text-xs text-stone-400 leading-relaxed"
            >
              En cliquant sur continuer, vous acceptez nos{' '}
              <Link
                to="/terms"
                className="underline underline-offset-4 transition-colors hover:text-stone-600"
              >
                Conditions d'utilisation
              </Link>{' '}
              et notre{' '}
              <a
                href="/privacy"
                className="underline underline-offset-4 transition-colors hover:text-stone-600"
              >
                Politique de confidentialité
              </a>
              .
            </motion.p>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
}
